import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Upload,
  Brain,
  Percent,
  Activity,
  Briefcase,
  Truck,
  BarChart2,
  DollarSign,
  MessageSquare,
  Send,
  X,
  Check,
  Printer,
  Bolt,
  Download,
  ChevronRight,
  Mail,
  Scissors,
  Zap,
  Play,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from "lucide-react";
import { processCsvData, convertExcelToCsv } from "./utils/analytics";
import {
  generateAIReport,
  askAI,
  generateMLPrediction,
} from "./services/geminiService";
import KPICard from "./components/KPICard";

// Material UI Imports
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Slider,
  TextField,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Fab,
  Fade,
  Collapse,
  alpha,
  Divider,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";

const FlotteDetails = () => {
  const theme = useTheme();

  // Data State
  const [analysis, setAnalysis] = useState(null);
  const [baselineAnalysis, setBaselineAnalysis] = useState(null);

  // Simulation State
  const [avgCost, setAvgCost] = useState(2500);
  const [expenseRatio, setExpenseRatio] = useState(25);
  const [commissionRatio, setCommissionRatio] = useState(15);
  const [deductible, setDeductible] = useState(0);
  const [premiumOverrides, setPremiumOverrides] = useState({});

  // App State
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [rawCsv, setRawCsv] = useState();
  const [fileName, setFileName] = useState();

  // Router / auto-load support: check for incoming fileId and download it
  const location = useLocation();
  const { flotteDownload } = useAuth();

  const jsonToCsv = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return "";
    const keys = Object.keys(arr[0]);
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (s.includes(",") || s.includes("\n") || s.includes('"')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const header = keys.join(",");
    const lines = arr.map((row) => keys.map((k) => escape(row[k])).join(","));
    return [header, ...lines].join("\n");
  };

  useEffect(() => {
    const fileId =
      location?.state?.fileId ||
      new URLSearchParams(location.search).get("fileId");
    if (!fileId) return;

    (async () => {
      try {
        setIsProcessing(true);
        const res = await flotteDownload(fileId);
        const data = res?.data;
        if (!data) {
          setError("Nessun dato disponibile per il file richiesto");
          return;
        }

        let file;
        if (Array.isArray(data)) {
          const csv = jsonToCsv(data);
          file = new File([csv], `${fileId}.csv`, { type: "text/csv" });
        } else if (typeof data === "string") {
          // assume CSV/text
          file = new File([data], `${fileId}.csv`, { type: "text/csv" });
        } else {
          const csv = jsonToCsv([data]);
          file = new File([csv], `${fileId}.csv`, { type: "text/csv" });
        }

        setFileName(`${fileId}.csv`);
        // processFile is defined elsewhere in this module and will run the analysis
        await processFile(file);
      } catch (err) {
        console.error(err);
        setError(err.message || "Errore scaricamento file");
      } finally {
        setIsProcessing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.fileId, location.search]);

  // Chat State
  const [chatHistory, setChatHistory] = useState([]);
  const [userQuestion, setUserQuestion] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false); // NEW: Chat visibility toggle
  const chatContainerRef = useRef(null);

  // Report State
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState("");

  // Drag and Drop state
  const [isDragging, setIsDragging] = useState(false);

  // ML Advanced State
  const [showMLModal, setShowMLModal] = useState(false);
  const [mlData, setMlData] = useState(null);
  const [isMLLoading, setIsMLLoading] = useState(false);

  // Custom Enrichment State
  const [customEnrichment, setCustomEnrichment] = useState({});

  // Colors for charts
  const VEHICLE_COLORS = [
    "#6366f1",
    "#f97316",
    "#10b981",
    "#ef4444",
    "#8b5cf6",
    "#64748b",
    "#ec4899",
    "#06b6d4",
    "#eab308",
    "#84cc16",
    "#f43f5e",
    "#a855f7",
  ];
  const CHART_COLORS = {
    primary: "#6366f1",
    secondary: "#8b5cf6",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6",
    grey: "#94a3b8",
  };

  /* useEffect(() => {
    handleProcessData(
      DEFAULT_CSV_DATA,
      avgCost,
      null,
      expenseRatio,
      commissionRatio,
      deductible,
      true
    );
  }, []); */

  // Scroll to bottom of chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isChatLoading, isChatOpen]);

  // Populate premiums on load
  useEffect(() => {
    if (analysis && Object.keys(premiumOverrides).length === 0) {
      const initialPremiums = {};

      // Populate from detailed stats
      analysis.stats_tipo_veicolo.forEach((s) => {
        // Calculate average premium for this type from the analysis data
        const avgPrem =
          s.Esposizione > 0 ? Math.round(s.Premio_Totale / s.Esposizione) : 0;
        initialPremiums[s.Tipo] = avgPrem;
      });

      setPremiumOverrides(initialPremiums);
    }
  }, [analysis]);

  const handleProcessData = async (
    csvContent,
    cost,
    premiums,
    expense,
    commission,
    deduct,
    isInitialLoad = false
  ) => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await processCsvData(
        csvContent,
        cost,
        premiums,
        expense,
        commission,
        deduct
      );
      setAnalysis(result);
      if (isInitialLoad || !baselineAnalysis) {
        setBaselineAnalysis(result);
      }
    } catch (err) {
      console.error(err);
      setError(
        "Errore nell'elaborazione del file. Controlla il formato del file (CSV o Excel) o la presenza dei dati necessari."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const processFile = async (file) => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setFileName(file.name);
      let content = "";

      const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");

      if (isExcel) {
        content = await convertExcelToCsv(file);
      } else {
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result);
          reader.onerror = reject;
          reader.readAsText(file);
        });
      }

      setRawCsv(content);
      setPremiumOverrides({});
      setChatHistory([]);
      setMlData(null);
      setCustomEnrichment({});
      setBaselineAnalysis(null);
      await handleProcessData(
        content,
        avgCost,
        null,
        expenseRatio,
        commissionRatio,
        deductible,
        true
      );
    } catch (err) {
      console.error(err);
      setError(
        typeof err === "string" ? err : "Errore durante la lettura del file."
      );
      setIsProcessing(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleApplySimulation = () => {
    handleProcessData(
      rawCsv,
      avgCost,
      premiumOverrides,
      expenseRatio,
      commissionRatio,
      deductible
    );
  };

  const handleGenerateReport = async () => {
    if (!analysis) return;
    setIsChatOpen(true); // Open chat when reporting starts
    setIsChatLoading(true);
    setChatHistory((prev) => [
      ...prev,
      {
        role: "user",
        text: "Genera un Report Strategico completo.",
        timestamp: new Date(),
      },
    ]);

    try {
      const report = await generateAIReport(analysis);
      setChatHistory((prev) => [
        ...prev,
        { role: "model", text: report, timestamp: new Date() },
      ]);
      setCurrentReport(report);
      setReportDialogOpen(true);
    } catch (err) {
      setChatHistory((prev) => [
        ...prev,
        {
          role: "model",
          text: "Errore nella generazione del report.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleRunML = async () => {
    if (!analysis) return;
    setIsMLLoading(true);
    try {
      const result = await generateMLPrediction(analysis, customEnrichment);
      setMlData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsMLLoading(false);
    }
  };

  const handlePrintMLReport = () => {
    if (!mlData) return;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      const shapHtml = mlData.shap_values
        .map(
          (s) =>
            `<tr>
            <td style="padding: 12px; border-bottom: 1px solid #eee;">
                <strong>${s.feature}</strong>
                <div style="font-size: 0.9em; color: #666;">${
                  s.description
                }</div>
            </td>
            <td style="padding: 12px; border-bottom: 1px solid #eee; color:${
              s.direction === "positive" ? "#ef4444" : "#10b981"
            }; font-weight: bold;">
                ${
                  s.direction === "positive"
                    ? "▲ Aumenta Rischio"
                    : "▼ Riduce Rischio"
                }
            </td>
        </tr>`
        )
        .join("");

      const roadmapHtml = mlData.missing_data_roadmap
        .map((r) => {
          const userValue = customEnrichment[r.field_name];
          return `<li style="margin-bottom: 10px;">
            <strong>${
              r.field_name
            }</strong> <span style="color: #666; font-size: 0.9em;">(${
            r.category
          })</span>
            <div style="margin-top: 4px;">${r.reason}</div>
            ${
              userValue
                ? `<div style="margin-top: 4px; color: #6366f1; font-weight: bold;">Valore Manuale: ${userValue}</div>`
                : ""
            }
        </li>`;
        })
        .join("");

      printWindow.document.write(`
        <html>
          <head>
            <title>Report ML Level 3 - InsurAnalytics Pro</title>
            <style>
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #1e293b; line-height: 1.6; max-width: 900px; margin: 0 auto; }
              h1 { color: #4f46e5; border-bottom: 3px solid #e2e8f0; padding-bottom: 15px; margin-bottom: 30px; }
              h2 { color: #4338ca; margin-top: 40px; border-left: 4px solid #8b5cf6; padding-left: 15px; }
              .header-meta { margin-bottom: 40px; color: #64748b; }
              
              .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 40px; }
              .kpi-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 25px; background: #f8fafc; text-align: center; }
              .kpi-value { font-size: 32px; font-weight: 800; color: #1e293b; margin: 10px 0; }
              .kpi-label { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; font-weight: 700; }
              
              table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px; }
              th { text-align: left; padding: 12px; background: #f1f5f9; color: #475569; font-weight: 700; }
              
              ul { padding-left: 20px; }
              
              .footer { margin-top: 60px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #94a3b8; text-align: center; }
              
              @media print {
                body { padding: 0; max-width: 100%; }
                .kpi-card { break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <h1>InsurAnalytics Pro • AI Studio Level 3</h1>
            <div class="header-meta">
              <strong>Simulazione XGBoost & Analisi Attuariale Avanzata</strong><br/>
              File Analizzato: ${fileName}<br/>
              Data: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
            </div>
            
            <div class="kpi-grid">
                <div class="kpi-card">
                    <div class="kpi-label">Frequenza Attesa (Poisson)</div>
                    <div class="kpi-value">${
                      mlData.predicted_frequency_poisson
                    }%</div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-label">Severity Attesa (Gamma)</div>
                    <div class="kpi-value">€ ${mlData.predicted_severity_gamma.toLocaleString()}</div>
                </div>
                <div class="kpi-card" style="background: #eef2ff; border-color: #c7d2fe;">
                    <div class="kpi-label" style="color: #4f46e5;">Pure Premium Target</div>
                    <div class="kpi-value" style="color: #4f46e5;">€ ${mlData.suggested_pure_premium.toLocaleString()}</div>
                </div>
            </div>

            <h2>Driver di Rischio (SHAP Values)</h2>
            <p>Analisi dei fattori che influenzano maggiormente la previsione del modello.</p>
            <table>
                <thead><tr><th>Feature / Descrizione</th><th>Direzione Impatto</th></tr></thead>
                <tbody>${shapHtml}</tbody>
            </table>

            <h2>Data Roadmap & Enrichment</h2>
            <p>Dati mancanti identificati per migliorare l'accuratezza del modello predittivo.</p>
            <ul>${roadmapHtml}</ul>
            
            <div class="footer">
              Report generato automaticamente da Google Gemini Models (2.5-Flash) su InsurAnalytics Pro.
            </div>
            
            <script>window.onload = function() { window.print(); }</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleEmail = () => {
    if (!analysis) return;
    const subject = `Report Analisi Portafoglio: ${fileName}`;
    const body = `
Resoconto Sintetico InsurAnalytics Pro
--------------------------------------
File Analizzato: ${fileName}
Polizze Totali: ${analysis.kpi_portafoglio.tot_polizze}

KPI PRINCIPALI (Storico 10 Anni):
- Frequenza Sinistri: ${analysis.kpi_portafoglio.frequenza_percent}%
- Loss Ratio: ${analysis.kpi_portafoglio.loss_ratio_percent}%
- Combined Ratio: ${analysis.kpi_portafoglio.combined_ratio_percent}%
- Profitto Tecnico Stimato: € ${analysis.kpi_portafoglio.profitto_tecnico.toLocaleString()}

PREVISIONE (12 Mesi):
- Sinistri Attesi: ${Math.round(
      analysis.forecast.reduce((a, b) => a + b.Sinistri_Previsti, 0)
    )}
- Costo Impatto Stimato: € ${analysis.forecast
      .reduce((a, b) => a + b.Costo_Stimato, 0)
      .toLocaleString()}

Generato da InsurAnalytics Pro.
      `.trim();
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const handlePrintReport = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      const htmlContent = currentReport
        .replace(/^### (.*$)/gim, "<h3>$1</h3>")
        .replace(/^## (.*$)/gim, "<h2>$1</h2>")
        .replace(/^# (.*$)/gim, "<h1>$1</h1>")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(/\n/g, "<br/>");

      printWindow.document.write(`
        <html>
          <head>
            <title>Report Strategico - InsurAnalytics Pro</title>
            <style>
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
              h1 { color: #1e40af; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; }
              h2 { color: #1e3a8a; margin-top: 20px; }
              h3 { color: #3b82f6; margin-top: 15px; }
              .meta { color: #64748b; margin-bottom: 30px; font-size: 0.9em; }
              .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 0.8em; color: #94a3b8; text-align: center; }
            </style>
          </head>
          <body>
            <h1>Report Strategico InsurAnalytics</h1>
            <div class="meta">
              <strong>File:</strong> ${fileName}<br/>
              <strong>Data Analisi:</strong> ${new Date().toLocaleDateString()}
            </div>
            <div>${htmlContent}</div>
            <div class="footer">
              Generato da InsurAnalytics Pro AI System
            </div>
            <script>
                window.onload = function() { window.print(); window.close(); }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleDownloadReport = () => {
    const element = document.createElement("a");
    const file = new Blob([currentReport], { type: "text/markdown" });
    element.href = URL.createObjectURL(file);
    element.download = `Report_Strategico_${fileName.split(".")[0]}_${new Date()
      .toISOString()
      .slice(0, 10)}.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleSendChat = async () => {
    if (!userQuestion.trim() || !analysis) return;

    const question = userQuestion;
    setUserQuestion("");
    setIsChatLoading(true);

    setChatHistory((prev) => [
      ...prev,
      { role: "user", text: question, timestamp: new Date() },
    ]);

    try {
      const jsonResponse = await askAI(analysis, question, chatHistory);
      let parsedData;
      try {
        parsedData = JSON.parse(jsonResponse);
      } catch (e) {
        console.warn("AI response was not valid JSON", e);
        parsedData = { answer: jsonResponse, proposed_action: null };
      }

      setChatHistory((prev) => [
        ...prev,
        {
          role: "model",
          text: parsedData.answer,
          action: parsedData.proposed_action,
          actionExecuted: false,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error(error);
      setChatHistory((prev) => [
        ...prev,
        {
          role: "model",
          text: "Spiacente, si è verificato un errore di connessione.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const executeAction = (index, action) => {
    let newCost = avgCost;
    let newDeductible = deductible;
    let newPremiums = { ...premiumOverrides };

    if (action.type === "UPDATE_COST") {
      newCost = action.value;
      setAvgCost(newCost);
    } else if (action.type === "UPDATE_DEDUCTIBLE") {
      newDeductible = action.value;
      setDeductible(newDeductible);
    } else if (action.type === "UPDATE_PREMIUMS") {
      const multiplier = 1 + action.value / 100;
      if (Object.keys(newPremiums).length === 0 && analysis) {
        analysis.stats_tipo_veicolo.forEach((s) => {
          newPremiums[s.Tipo] =
            s.Esposizione > 0
              ? Math.round(s.Premio_Totale / s.Esposizione)
              : 500;
        });
      }
      Object.keys(newPremiums).forEach((k) => {
        newPremiums[k] = Math.round(newPremiums[k] * multiplier);
      });
      setPremiumOverrides(newPremiums);
    }

    handleProcessData(
      rawCsv,
      newCost,
      newPremiums,
      expenseRatio,
      commissionRatio,
      newDeductible
    );
    setChatHistory((prev) =>
      prev.map((msg, i) =>
        i === index ? { ...msg, actionExecuted: true } : msg
      )
    );
  };

  const dismissAction = (index) => {
    setChatHistory((prev) =>
      prev.map((msg, i) =>
        i === index ? { ...msg, actionExecuted: true } : msg
      )
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const fmtMoney = (val) =>
    `€ ${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const premiumCardStyle = {
    borderRadius: 4,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
  };

  const renderCustomPieLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, index } =
      props;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.02) return null; // Don't show very small slices

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={9}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (!analysis && isProcessing && !error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{ color: "primary.main" }}
        />
        <Typography variant="h6" color="text.secondary" fontWeight="500">
          Inizializzazione InsurAnalytics...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* HEADER RESTORED */}
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}
        className="print-hidden"
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: "space-between",
              py: 1.5,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box color="primary.main">
                <Activity size={32} strokeWidth={2.5} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight="800"
                  color="text.primary"
                  lineHeight={1.1}
                  letterSpacing="-0.02em"
                >
                  Mobility Analytics Pro
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="500"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <Briefcase size={12} /> File: {fileName}
                </Typography>
              </Box>
            </Stack>

            <Stack
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box
                display="flex"
                gap={2}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx,.xls"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />

                <Button
                  variant="outlined"
                  startIcon={<Upload size={18} />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    borderRadius: 2,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    px: 2,
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                >
                  CARICA FILE
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Printer size={18} />}
                  onClick={handlePrint}
                  sx={{
                    borderRadius: 2,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    px: 2,
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                >
                  STAMPA
                </Button>
                <Button
                  onClick={handleEmail}
                  variant="outlined"
                  startIcon={<Mail size={18} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    px: 2,
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                >
                  EMAIL
                </Button>
              </Box>
              <Box
                display="flex"
                gap={2}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<Zap size={18} />}
                  onClick={() => setShowMLModal(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    px: 2,
                    bgcolor: "#8b5cf6",
                    "&:hover": { bgcolor: "#7c3aed" },
                  }}
                >
                  PREDICT ADVANCED
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<Brain size={18} />}
                  onClick={handleGenerateReport}
                  disabled={isChatLoading || !analysis}
                  sx={{
                    borderRadius: 2,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    px: 2,
                  }}
                >
                  REPORT STRATEGICO
                </Button>
              </Box>
            </Stack>
          </Toolbar>
        </Container>
      </Box>

      <Box display="flex" flexDirection="column" gap={4}>
        {error && (
          <Paper
            sx={{
              p: 2,
              mb: 4,
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
              display: "flex",
              gap: 2,
              alignItems: "center",
              borderRadius: 3,
            }}
            className="print-hidden"
          >
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {/* RESTORED CONTROL PANEL */}
        <Paper sx={{ ...premiumCardStyle, p: 4 }} className="print-hidden">
          <Grid container spacing={{ xs: 3, md: 6 }}>
            {/* LEFT COLUMN: KPI SLIDERS */}
            <Grid item xs={12} lg={6} sx={{ pr: { md: 4 } }}>
              <Grid container spacing={4} rowSpacing={4}>
                {/* 1. Costo Medio - Purple */}
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Activity size={18} color="#e67e22" />
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="text.primary"
                      >
                        Costo Medio Sinistro
                      </Typography>
                      <Chip
                        label={fmtMoney(avgCost)}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: "#e67e22",
                          borderColor: "#e67e22",
                          fontWeight: "bold",
                          height: 20,
                        }}
                      />
                    </Stack>
                    <Slider
                      value={avgCost}
                      min={500}
                      max={10000}
                      step={100}
                      onChange={(_, val) => setAvgCost(val)}
                      onChangeCommitted={handleApplySimulation}
                      sx={{
                        color: "#9c27b0",
                        height: 6,
                        "& .MuiSlider-thumb": { width: 16, height: 16 },
                      }}
                    />
                  </Stack>
                </Grid>

                {/* 2. Franchigia - Red */}
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Scissors size={18} color="#d32f2f" />
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="text.primary"
                      >
                        Franchigia Media
                      </Typography>
                      <Chip
                        label={fmtMoney(deductible)}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: "#d32f2f",
                          borderColor: "#d32f2f",
                          fontWeight: "bold",
                          height: 20,
                        }}
                      />
                    </Stack>
                    <Slider
                      value={deductible}
                      min={0}
                      max={1500}
                      step={50}
                      onChange={(_, val) => setDeductible(val)}
                      onChangeCommitted={handleApplySimulation}
                      sx={{
                        color: "#ef5350",
                        height: 6,
                        "& .MuiSlider-thumb": { width: 16, height: 16 },
                      }}
                    />
                  </Stack>
                </Grid>

                {/* 3. Expense Ratio - Green */}
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Percent size={18} color="#2e7d32" />
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="text.primary"
                      >
                        % Expense Ratio (%)
                      </Typography>
                      <Chip
                        label={`${expenseRatio}%`}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: "#2e7d32",
                          borderColor: "#2e7d32",
                          fontWeight: "bold",
                          height: 20,
                        }}
                      />
                    </Stack>
                    <Slider
                      value={expenseRatio}
                      min={0}
                      max={50}
                      step={1}
                      onChange={(_, val) => setExpenseRatio(val)}
                      onChangeCommitted={handleApplySimulation}
                      sx={{
                        color: "#2e7d32",
                        height: 6,
                        "& .MuiSlider-thumb": { width: 16, height: 16 },
                      }}
                    />
                  </Stack>
                </Grid>

                {/* 4. Commission - Blue */}
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DollarSign size={18} color="#1976d2" />
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="text.primary"
                      >
                        $ Commission Ratio (%)
                      </Typography>
                      <Chip
                        label={`${commissionRatio}%`}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: "#1976d2",
                          borderColor: "#1976d2",
                          fontWeight: "bold",
                          height: 20,
                        }}
                      />
                    </Stack>
                    <Slider
                      value={commissionRatio}
                      min={0}
                      max={40}
                      step={1}
                      onChange={(_, val) => setCommissionRatio(val)}
                      onChangeCommitted={handleApplySimulation}
                      sx={{
                        color: "#1976d2",
                        height: 6,
                        "& .MuiSlider-thumb": { width: 16, height: 16 },
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            {/* RIGHT COLUMN: PREMI INPUTS */}
            <Grid item xs={12} lg={6}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                my={3}
              >
                <Stack
                  direction="row"
                  display="flex"
                  alignItems="center"
                  spacing={1}
                >
                  <Box
                    p={0.5}
                    borderRadius={1}
                    bgcolor="rgba(59, 130, 246, 0.1)"
                  >
                    <Bolt size={18} color={theme.palette.info.main} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight="700">
                    Premi Medi per Categoria
                  </Typography>
                </Stack>
                <Button
                  variant="text"
                  startIcon={<Play size={14} fill="currentColor" />}
                  size="small"
                  onClick={handleApplySimulation}
                  sx={{ fontWeight: "bold", fontSize: 11 }}
                >
                  RICALCOLA KPI
                </Button>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              {/* Scrollable Container for Dynamic Inputs */}
              <Box sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  {analysis?.stats_tipo_veicolo.map((stat) => (
                    <Grid item xs={12} sm={6} key={stat.Tipo}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        mb={0.5}
                        display="block"
                        sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                      >
                        {stat.Tipo}
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="number"
                        value={
                          premiumOverrides[stat.Tipo] !== undefined
                            ? premiumOverrides[stat.Tipo]
                            : ""
                        }
                        onChange={(e) =>
                          setPremiumOverrides((prev) => ({
                            ...prev,
                            [stat.Tipo]: Number(e.target.value),
                          }))
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="caption">€</Typography>
                            </InputAdornment>
                          ),
                          sx: {
                            borderRadius: 2,
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {analysis && (
          <Box>
            {/* KPI Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <KPICard
                  title="Profitto Tecnico"
                  value={fmtMoney(analysis.kpi_portafoglio.profitto_tecnico)}
                  baselineValue={
                    baselineAnalysis?.kpi_portafoglio.profitto_tecnico
                  }
                  icon={DollarSign}
                  borderColor={
                    analysis.kpi_portafoglio.profitto_tecnico >= 0
                      ? CHART_COLORS.success
                      : CHART_COLORS.error
                  }
                  subValue={`Margine: ${(
                    (analysis.kpi_portafoglio.profitto_tecnico /
                      analysis.kpi_portafoglio.tot_premi) *
                    100
                  ).toFixed(1)}% (${fmtMoney(
                    analysis.kpi_portafoglio.premio_annuale_corrente *
                      (analysis.kpi_portafoglio.profitto_tecnico /
                        analysis.kpi_portafoglio.tot_premi)
                  )})`}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KPICard
                  title="Combined Ratio"
                  value={`${analysis.kpi_portafoglio.combined_ratio_percent}%`}
                  baselineValue={
                    baselineAnalysis?.kpi_portafoglio.combined_ratio_percent
                  }
                  inverseTrend={true}
                  icon={Percent}
                  borderColor={
                    analysis.kpi_portafoglio.combined_ratio_percent < 100
                      ? CHART_COLORS.secondary
                      : CHART_COLORS.error
                  }
                  subValue={`(${fmtMoney(
                    analysis.kpi_portafoglio.premio_annuale_corrente *
                      (analysis.kpi_portafoglio.loss_ratio_percent / 100)
                  )}) LR: ${
                    analysis.kpi_portafoglio.loss_ratio_percent
                  }% + Exp: ${
                    analysis.kpi_portafoglio.expense_ratio_percent
                  }% + Comm: ${
                    analysis.kpi_portafoglio.commission_ratio_percent
                  }%`}
                  formatFn={(v) => `${v}%`}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KPICard
                  title="Frequenza Sinistri"
                  value={`${analysis.kpi_portafoglio.frequenza_percent}%`}
                  baselineValue={
                    baselineAnalysis?.kpi_portafoglio.frequenza_percent
                  }
                  inverseTrend={true}
                  icon={Activity}
                  borderColor={CHART_COLORS.info}
                  subValue={`${analysis.kpi_portafoglio.tot_sinistri} Sinistri / ${analysis.kpi_portafoglio.tot_polizze} Polizze`}
                  formatFn={(v) => `${v}%`}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KPICard
                  title="Valore Portafoglio"
                  value={fmtMoney(analysis.kpi_portafoglio.tot_premi)}
                  baselineValue={baselineAnalysis?.kpi_portafoglio.tot_premi}
                  icon={Briefcase}
                  borderColor={CHART_COLORS.grey}
                  subValue={`Snapshot Annuo: ${fmtMoney(
                    analysis.kpi_portafoglio.premio_annuale_corrente
                  )}`}
                />
              </Grid>
            </Grid>

            {/* HERO ROW: Historical Analysis (Full Width) */}
            <Card sx={{ ...premiumCardStyle, mb: 4 }}>
              <CardHeader
                title="Analisi Storica Attestati (10 Anni)"
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                subheader="Trend di frequenza e volume osservato sui veicoli in portafoglio"
                action={
                  <Stack direction="row" spacing={1} alignItems="center" mr={2}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box
                        width={12}
                        height={12}
                        bgcolor="#e2e8f0"
                        borderRadius={0.5}
                      />
                      <Typography variant="caption">
                        Veicoli Osservati
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box
                        width={12}
                        height={12}
                        bgcolor={CHART_COLORS.primary}
                        borderRadius={0.5}
                      />
                      <Typography variant="caption">Freq %</Typography>
                    </Stack>
                  </Stack>
                }
              />
              <CardContent>
                <Box height={450}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={analysis.analisi_annuale_atr}
                      margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f1f5f9"
                      />
                      <XAxis
                        dataKey="Anno"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontWeight: 500 }}
                      />
                      <YAxis yAxisId="left" hide />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b" }}
                        unit="%"
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "#202069ff",
                          borderRadius: "12px",
                          border: "none",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        }}
                        itemStyle={{ fontSize: 13, fontWeight: 500 }}
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="Veicoli_Osservati"
                        name="Veicoli Osservati"
                        fill="#e2e8f0"
                        radius={[6, 6, 0, 0]}
                        barSize={50}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="Frequenza_Totale"
                        name="Freq. Totale %"
                        stroke={CHART_COLORS.primary}
                        strokeWidth={4}
                        dot={{
                          fill: CHART_COLORS.primary,
                          r: 6,
                          strokeWidth: 2,
                          stroke: "#fff",
                        }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="Frequenza_Principale"
                        name="Freq. Responsabilità %"
                        stroke={CHART_COLORS.warning}
                        strokeWidth={3}
                        dot={false}
                        strokeDasharray="5 5"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>

            {/* ROW 2: Portfolio Composition & Technical Performance (50/50 Split) */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Portfolio Composition */}
              <Grid item xs={12} md={6} lg={6}>
                <Card sx={{ ...premiumCardStyle, height: "100%" }}>
                  <CardHeader
                    title="Composizione Portafoglio"
                    subheader="Distribuzione per Tipo Veicolo"
                    titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                    avatar={
                      <Box
                        p={1}
                        bgcolor="rgba(99,102,241,0.1)"
                        borderRadius={1.5}
                      >
                        <Truck size={20} color={CHART_COLORS.primary} />
                      </Box>
                    }
                  />
                  <CardContent>
                    <Box height={400}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={analysis.stats_tipo_veicolo}
                            cx="50%"
                            cy="50%"
                            innerRadius={80}
                            outerRadius={130}
                            paddingAngle={2}
                            dataKey="Numero_Polizze"
                            nameKey="Tipo"
                            label={renderCustomPieLabel}
                            labelLine={false}
                          >
                            {analysis.stats_tipo_veicolo.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  VEHICLE_COLORS[index % VEHICLE_COLORS.length]
                                }
                                strokeWidth={2}
                                stroke="#fff"
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              borderRadius: "8px",
                              border: "none",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                            }}
                            formatter={(value, name, props) => {
                              const total =
                                analysis.kpi_portafoglio.tot_polizze;
                              const percent = ((value / total) * 100).toFixed(
                                1
                              );
                              return [`${value} Mezzi (${percent}%)`, name];
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={100}
                            iconType="circle"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Technical Performance */}
              <Grid item xs={12} md={6} lg={6}>
                <Card sx={{ ...premiumCardStyle, height: "100%" }}>
                  <CardHeader
                    title="Performance Tecnica"
                    subheader="Confronto Frequenza vs Loss Ratio"
                    titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                    avatar={
                      <Box
                        p={1}
                        bgcolor="rgba(16,185,129,0.1)"
                        borderRadius={1.5}
                      >
                        <BarChart2 size={20} color={CHART_COLORS.success} />
                      </Box>
                    }
                  />
                  <CardContent>
                    <Box height={400}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={analysis.stats_tipo_veicolo}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            horizontal={true}
                            vertical={false}
                            stroke="#f1f5f9"
                          />
                          <XAxis type="number" hide />
                          <YAxis
                            dataKey="Tipo"
                            type="category"
                            tick={{
                              fontSize: 10,
                              fill: "#64748b",
                              fontWeight: 600,
                            }}
                            width={140}
                          />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "#202069ff",
                              borderRadius: "8px",
                              border: "none",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                            }}
                            formatter={(value, name, item) => {
                              if (name === "Frequenza %")
                                return [`${value}%`, name];
                              if (name === "Loss Ratio %")
                                return [`${value}%`, name];
                              return [value, name];
                            }}
                            labelFormatter={(label, payload) => {
                              if (payload && payload.length > 0) {
                                const dataItem = payload[0].payload;
                                return `${label} (${dataItem.Numero_Polizze} Mezzi)`;
                              }
                              return label;
                            }}
                          />
                          <Legend verticalAlign="top" align="right" />
                          <Bar
                            dataKey="Loss_Ratio_percent"
                            name="Loss Ratio %"
                            fill={CHART_COLORS.warning}
                            radius={[0, 4, 4, 0]}
                            barSize={15}
                          />
                          <Bar
                            dataKey="Frequenza_percent"
                            name="Frequenza %"
                            fill={CHART_COLORS.primary}
                            radius={[0, 4, 4, 0]}
                            barSize={15}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* ROW 3: Monthly Trends & Forecast (Full Width Stacked) */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <Card sx={{ ...premiumCardStyle }}>
                  <CardHeader
                    title="Andamento Tecnico Mensile"
                    titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                  />
                  <CardContent>
                    <Box height={350}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={analysis.freq_mensile}
                          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#f1f5f9"
                          />
                          <XAxis
                            dataKey="Mese"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: "#94a3b8" }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: "#94a3b8" }}
                            unit="%"
                          />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "#202069ff",
                              borderRadius: "8px",
                              border: "none",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="Loss_Ratio_percent"
                            name="Loss Ratio %"
                            stroke={CHART_COLORS.warning}
                            strokeWidth={2}
                            dot={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="Combined_Ratio_percent"
                            name="Combined Ratio %"
                            stroke={CHART_COLORS.secondary}
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ ...premiumCardStyle }}>
                  <CardHeader
                    title="Previsione Sinistri (12 Mesi)"
                    titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                    action={
                      <Chip
                        label="AI Forecast"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        icon={<Bolt size={12} />}
                      />
                    }
                  />
                  <CardContent>
                    <Box height={350}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={analysis.forecast}
                          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient
                              id="colorSinistri"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor={CHART_COLORS.secondary}
                                stopOpacity={0.2}
                              />
                              <stop
                                offset="95%"
                                stopColor={CHART_COLORS.secondary}
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#f1f5f9"
                          />
                          <XAxis
                            dataKey="Mese"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: "#94a3b8" }}
                            tickMargin={10}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: "#94a3b8" }}
                          />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: "#202069ff",
                              borderRadius: "8px",
                              border: "none",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="Sinistri_Previsti"
                            stroke={CHART_COLORS.secondary}
                            strokeWidth={3}
                            fillOpacity={1}
                            fill="url(#colorSinistri)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Footer / Table Area */}
            <Card
              sx={{ ...premiumCardStyle, mb: 8 }}
              className="print-break-inside-avoid"
            >
              <CardHeader
                title="Dettaglio Annuale Sinistrosità"
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Anno
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Veicoli
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Sinistri
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Costo (Est)
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Premi (As-If)
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Profitto (As-If)
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Frequenza
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysis.analisi_annuale_atr.map((row) => (
                      <TableRow
                        key={row.Anno}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell sx={{ fontWeight: "medium" }}>
                          {row.Anno}
                        </TableCell>
                        <TableCell>{row.Veicoli_Osservati}</TableCell>
                        <TableCell>{row.Numero_Sinistri_Totali}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {fmtMoney(row.Costo_Sinistri_Totale)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {fmtMoney(row.Monte_Premi_Stimato)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: "bold",
                            color:
                              row.Profitto_Tecnico_Stimato >= 0
                                ? "success.main"
                                : "error.main",
                          }}
                        >
                          {fmtMoney(row.Profitto_Tecnico_Stimato)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${row.Frequenza_Totale}%`}
                            size="small"
                            sx={{
                              bgcolor:
                                row.Frequenza_Totale > 10
                                  ? "rgba(239, 68, 68, 0.1)"
                                  : row.Frequenza_Totale > 5
                                  ? "rgba(245, 158, 11, 0.1)"
                                  : "rgba(16, 185, 129, 0.1)",
                              color:
                                row.Frequenza_Totale > 10
                                  ? "error.main"
                                  : row.Frequenza_Totale > 5
                                  ? "warning.main"
                                  : "success.main",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* AI Chat Drawer / Floating Button */}
            <Box
              sx={{ position: "fixed", bottom: 32, right: 32, zIndex: 1200 }}
              className="print-hidden"
            >
              <Fade in={!isChatOpen}>
                <Fab
                  color="primary"
                  aria-label="chat"
                  onClick={() => setIsChatOpen(true)}
                  sx={{ boxShadow: "0 4px 20px rgba(99, 102, 241, 0.4)" }}
                >
                  <MessageSquare />
                </Fab>
              </Fade>
            </Box>

            <Fade in={isChatOpen} mountOnEnter unmountOnExit>
              <Paper
                elevation={12}
                sx={{
                  position: "fixed",
                  bottom: 32,
                  right: 32,
                  width: 380,
                  height: 600,
                  borderRadius: 4,
                  zIndex: 1200,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
                className="print-hidden"
              >
                {/* Chat Header */}
                <Box
                  p={2}
                  bgcolor="primary.main"
                  color="white"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      p={0.5}
                      bgcolor="rgba(255,255,255,0.2)"
                      borderRadius={1}
                    >
                      <Brain size={18} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        lineHeight={1.2}
                      >
                        Agente Attuario AI
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Mobility Analytics Pro
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => setIsChatOpen(false)}
                    sx={{
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    <X size={18} />
                  </IconButton>
                </Box>

                {/* Chat Messages */}
                <Box
                  ref={chatContainerRef}
                  flex={1}
                  overflow="auto"
                  p={2}
                  display="flex"
                  flexDirection="column"
                  gap={2}
                >
                  {chatHistory.length === 0 ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                      color="text.secondary"
                      sx={{ opacity: 0.6 }}
                    >
                      <Brain
                        size={48}
                        style={{
                          marginBottom: 16,
                          color: theme.palette.primary.light,
                        }}
                      />
                      <Typography
                        variant="body2"
                        align="center"
                        fontWeight="500"
                      >
                        Come posso aiutarti oggi?
                      </Typography>
                      <Typography variant="caption" align="center">
                        Chiedi analisi sui dati o simulazioni
                      </Typography>
                    </Box>
                  ) : (
                    chatHistory.map((msg, i) => (
                      <Box
                        key={i}
                        alignSelf={
                          msg.role === "user" ? "flex-end" : "flex-start"
                        }
                        maxWidth="85%"
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderTopRightRadius: msg.role === "user" ? 0 : 12,
                            borderTopLeftRadius: msg.role === "model" ? 0 : 12,
                            bgcolor: msg.role === "user" ? "primary.main" : "",
                            color: msg.role === "user" ? "" : "text.primary",
                            border:
                              msg.role === "model"
                                ? "1px solid #e2e8f0"
                                : "none",
                            boxShadow:
                              msg.role === "model"
                                ? "0 2px 4px rgba(0,0,0,0.05)"
                                : "none",
                          }}
                        >
                          {msg.role === "model" ? (
                            <Typography
                              variant="body2"
                              component="div"
                              dangerouslySetInnerHTML={{
                                __html: msg.text
                                  .replace(
                                    /\*\*(.*?)\*\*/g,
                                    "<strong>$1</strong>"
                                  )
                                  .replace(/\n/g, "<br/>"),
                              }}
                            />
                          ) : (
                            <Typography variant="body2">{msg.text}</Typography>
                          )}

                          {msg.action && !msg.actionExecuted && (
                            <Box
                              mt={2}
                              p={1.5}
                              border="1px solid"
                              borderColor="primary.light"
                              borderRadius={2}
                              bgcolor="primary.50"
                              color="text.primary"
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                mb={1}
                              >
                                <Bolt color="#f59e0b" size={16} />
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  color="primary.dark"
                                  textTransform="uppercase"
                                >
                                  Azione Proposta
                                </Typography>
                              </Stack>
                              <Typography
                                variant="body2"
                                mb={1.5}
                                color="text.primary"
                                fontSize="0.85rem"
                              >
                                {msg.action.reason}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() =>
                                    msg.action && executeAction(i, msg.action)
                                  }
                                  startIcon={<Check size={14} />}
                                  color="primary"
                                  sx={{ flex: 1 }}
                                >
                                  Applica
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={() => dismissAction(i)}
                                  sx={{ minWidth: 40 }}
                                >
                                  <X size={14} />
                                </Button>
                              </Stack>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    ))
                  )}
                  {isChatLoading && (
                    <Box alignSelf="flex-start">
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          bgcolor: "white",
                        }}
                      >
                        <Stack direction="row" spacing={0.5}>
                          <Box
                            width={6}
                            height={6}
                            bgcolor="text.disabled"
                            borderRadius="50%"
                            sx={{ animation: "bounce 1s infinite" }}
                          />
                          <Box
                            width={6}
                            height={6}
                            bgcolor="text.disabled"
                            borderRadius="50%"
                            sx={{ animation: "bounce 1s infinite 0.2s" }}
                          />
                          <Box
                            width={6}
                            height={6}
                            bgcolor="text.disabled"
                            borderRadius="50%"
                            sx={{ animation: "bounce 1s infinite 0.4s" }}
                          />
                        </Stack>
                      </Paper>
                    </Box>
                  )}
                </Box>

                {/* Chat Input */}
                <Box p={2} borderTop={1} borderColor="divider">
                  <Stack direction="row" spacing={1}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Scrivi qui..."
                      value={userQuestion}
                      onChange={(e) => setUserQuestion(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSendChat()}
                      disabled={isChatLoading}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSendChat}
                      disabled={isChatLoading || !userQuestion.trim()}
                      sx={{ bgcolor: "primary.50" }}
                    >
                      <Send size={18} />
                    </IconButton>
                  </Stack>
                </Box>
              </Paper>
            </Fade>
          </Box>
        )}
      </Box>

      {/* ML Prediction Dialog */}
      <Dialog
        open={showMLModal}
        onClose={() => setShowMLModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, minHeight: "80vh" } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#8b5cf6",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box p={1} bgcolor="rgba(255,255,255,0.2)" borderRadius={2}>
              <Zap size={24} strokeWidth={3} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="800">
                AI Insurance Studio • Level 3
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.9, fontWeight: 500 }}
              >
                Predittiva Avanzata (XGBoost Simulation) & Pricing Dinamico
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={() => setShowMLModal(false)}
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <X />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
          {!mlData && !isMLLoading ? (
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={3}
              py={10}
            >
              <Box
                p={4}
                bgcolor="white"
                borderRadius="50%"
                boxShadow="0 20px 40px rgba(139, 92, 246, 0.15)"
              >
                <Zap size={64} color="#8b5cf6" strokeWidth={1.5} />
              </Box>
              <Typography
                variant="h4"
                fontWeight="800"
                color="text.primary"
                align="center"
              >
                Pronto per la Simulazione
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                align="center"
                maxWidth={600}
              >
                Il sistema utilizzerà un modello{" "}
                <strong>XGBoost (Simulato)</strong> per calcolare la frequenza
                attesa (Poisson) e il costo medio (Gamma) basandosi sui dati
                arricchiti del portafoglio.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleRunML}
                startIcon={<Play size={24} fill="currentColor" />}
                sx={{
                  mt: 2,
                  py: 2,
                  px: 6,
                  fontSize: "1.1rem",
                  borderRadius: 4,
                  fontWeight: 800,
                  bgcolor: "#8b5cf6",
                  boxShadow: "0 10px 30px rgba(139, 92, 246, 0.4)",
                  "&:hover": {
                    bgcolor: "#7c3aed",
                    transform: "translateY(-2px)",
                    boxShadow: "0 15px 35px rgba(139, 92, 246, 0.5)",
                  },
                  transition: "all 0.2s",
                }}
              >
                AVVIA SIMULAZIONE ML
              </Button>
            </Box>
          ) : isMLLoading ? (
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={4}
              py={10}
            >
              <CircularProgress
                size={80}
                thickness={3}
                sx={{ color: "#8b5cf6" }}
              />
              <Typography variant="h5" fontWeight="600" color="text.secondary">
                Elaborazione Modelli Predittivi in corso...
              </Typography>
              <Stack spacing={1} width={300}>
                <LinearProgress
                  variant="indeterminate"
                  sx={{
                    bgcolor: "#ddd6fe",
                    "& .MuiLinearProgress-bar": { bgcolor: "#8b5cf6" },
                  }}
                />
                <Typography
                  variant="caption"
                  align="center"
                  color="text.disabled"
                >
                  Calcolo SHAP Values • Ottimizzazione Pricing
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Box>
              {/* Dashboard Content */}
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {/* KPI Row */}
                <Grid xs={12} md={4}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 4,
                      height: "100%",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="overline"
                        fontWeight="700"
                        color="text.secondary"
                      >
                        Frequenza Predetta (Poisson)
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        spacing={1}
                        mt={1}
                      >
                        <Typography
                          variant="h3"
                          fontWeight="800"
                          color="#8b5cf6"
                        >
                          {mlData?.predicted_frequency_poisson}%
                        </Typography>
                        <Chip
                          label={
                            analysis
                              ? `${(
                                  mlData.predicted_frequency_poisson -
                                  analysis.kpi_portafoglio.frequenza_percent
                                ).toFixed(2)}% vs Attuale`
                              : ""
                          }
                          size="small"
                          color={
                            mlData.predicted_frequency_poisson >
                            (analysis?.kpi_portafoglio.frequenza_percent || 0)
                              ? "error"
                              : "success"
                          }
                          variant="outlined"
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} md={4}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 4,
                      height: "100%",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="overline"
                        fontWeight="700"
                        color="text.secondary"
                      >
                        Costo Medio Atteso (Gamma)
                      </Typography>
                      <Typography
                        variant="h3"
                        fontWeight="800"
                        color="text.primary"
                        mt={1}
                      >
                        € {mlData?.predicted_severity_gamma.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} md={4}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #8b5cf6",
                      bgcolor: "rgba(139, 92, 246, 0.05)",
                      borderRadius: 4,
                      height: "100%",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="overline"
                        fontWeight="700"
                        color="primary.main"
                      >
                        Pure Premium Suggerito
                      </Typography>
                      <Typography
                        variant="h3"
                        fontWeight="800"
                        color="primary.main"
                        mt={1}
                      >
                        € {mlData?.suggested_pure_premium.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* SHAP Values & Missing Data */}
                <Grid xs={12} lg={8}>
                  <Card
                    elevation={0}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: 4 }}
                  >
                    <CardHeader
                      title="Feature Importance (SHAP Values)"
                      titleTypographyProps={{ fontWeight: "bold" }}
                    />
                    <CardContent>
                      <Stack spacing={2}>
                        {mlData?.shap_values.map((shap, idx) => (
                          <Box
                            key={idx}
                            p={2}
                            border="1px solid #f1f5f9"
                            borderRadius={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {shap.feature}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {shap.description}
                              </Typography>
                            </Stack>
                            <Chip
                              label={
                                shap.direction === "positive"
                                  ? "Aumenta Rischio"
                                  : "Riduce Rischio"
                              }
                              color={
                                shap.direction === "positive"
                                  ? "error"
                                  : "success"
                              }
                              variant="filled"
                              size="small"
                              icon={
                                shap.direction === "positive" ? (
                                  <ArrowUpRight size={14} />
                                ) : (
                                  <ArrowDownRight size={14} />
                                )
                              }
                              sx={{ fontWeight: "bold" }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid xs={12} lg={4}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 4,
                      height: "100%",
                    }}
                  >
                    <CardHeader
                      title="Data Enrichment Roadmap"
                      titleTypographyProps={{
                        fontWeight: "bold",
                        fontSize: "1rem",
                      }}
                      subheader="Dati mancanti per migliorare il modello"
                    />
                    <CardContent>
                      <Stack spacing={2}>
                        {mlData?.missing_data_roadmap.map((item, idx) => (
                          <Box key={idx}>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="flex-start"
                              mb={1}
                            >
                              <Box
                                minWidth={4}
                                height={40}
                                bgcolor="#cbd5e1"
                                borderRadius={2}
                              />
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="700"
                                  color="text.primary"
                                >
                                  {item.field_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="500"
                                  display="block"
                                >
                                  {item.category}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    display: "block",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {item.reason}
                                </Typography>
                              </Box>
                            </Stack>
                            <TextField
                              size="small"
                              placeholder="Inserisci valore manuale..."
                              value={customEnrichment[item.field_name] || ""}
                              onChange={(e) =>
                                setCustomEnrichment((prev) => ({
                                  ...prev,
                                  [item.field_name]: e.target.value,
                                }))
                              }
                              fullWidth
                              InputProps={{
                                sx: { fontSize: "0.85rem" },
                              }}
                            />
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          startIcon={<RefreshCw size={16} />}
                          onClick={handleRunML}
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          Rilancia Simulazione con Dati
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        {/* Added DialogActions with Print Button */}
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setShowMLModal(false)}
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            Chiudi
          </Button>
          <Button
            variant="contained"
            onClick={handlePrintMLReport}
            disabled={!mlData || isMLLoading}
            startIcon={<Printer />}
          >
            Stampa Report ML
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "primary.50",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Brain color={theme.palette.primary.main} />
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              Report Strategico AI
            </Typography>
          </Stack>
          <IconButton onClick={() => setReportDialogOpen(false)} size="small">
            <X />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, md: 4 } }}>
          <Typography
            component="div"
            variant="body1"
            color="text.primary"
            sx={{ whiteSpace: "pre-line" }}
          >
            {currentReport.split("\n").map((line, i) => {
              if (line.startsWith("### "))
                return (
                  <Typography
                    key={i}
                    variant="h6"
                    sx={{
                      mt: 2,
                      mb: 1,
                      color: "primary.main",
                      backgroundColor: "black",
                      fontWeight: "bold",
                    }}
                  >
                    {line.replace("### ", "")}
                  </Typography>
                );
              if (line.startsWith("## "))
                return (
                  <Typography
                    key={i}
                    variant="h5"
                    sx={{
                      mt: 3,
                      mb: 1,
                      color: "primary.dark",
                      fontWeight: "bold",
                    }}
                  >
                    {line.replace("## ", "")}
                  </Typography>
                );
              if (line.startsWith("**") && line.endsWith("**"))
                return (
                  <Typography
                    key={i}
                    variant="subtitle1"
                    sx={{ mt: 2, fontWeight: "bold" }}
                  >
                    {line.replace(/\*\*/g, "")}
                  </Typography>
                );
              return (
                <Typography
                  key={i}
                  variant="body1"
                  paragraph
                  dangerouslySetInnerHTML={{
                    __html: line.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>"),
                  }}
                />
              );
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "grey.50" }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadReport}
          >
            Scarica Markdown
          </Button>
          <Button
            variant="contained"
            startIcon={<Printer />}
            onClick={handlePrintReport}
          >
            Stampa Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlotteDetails;
